import {
    SortingState,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getPaginationRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { useState, useEffect, useMemo } from "react";
import { FaSortUp } from "react-icons/fa";
import { FaSortDown } from "react-icons/fa";
import { Flex } from "../../../../components/Flex";
import { Icon } from "../../../../components/Icon";
import { useParams } from "react-router-dom";
import { useOrganisationData } from "../../../../data/useOrganisationData";
import { SubjectTypeIcon } from "../../../../components/SubjectTypeIcon";
import "../styles.css";
import { Paragraph } from "../../../../components/Paragraph";
import { CgChevronDown, CgChevronUp } from "react-icons/cg";
import React from "react";
import { Loader } from "../../../../components/Loader";
import { formatDate } from "../../../../utils/formatters";

export const OrgListTab = (props) => {
    const { searchTerm, filterUserIds, filters } = props;

    const { id } = useParams<{ id: string }>();
    const profileId = Number(id);

    const validProfileId = Number.isFinite(profileId) ? profileId : 0;
    const orgQuery = useOrganisationData(validProfileId);
    const fetchedorg = orgQuery.data;
    const [expanded, setExpanded] = useState({});

    const [data, setData] = useState<any>({});
    const cols = [
        {
            header: () => "",
            accessorKey: "display_name",
            cell: (row) => {
                return (
                    <>
                        <Flex alignItems="center" gap="6">
                            <SubjectTypeIcon
                                isEntity={row?.row?.original?.type === "entity"}
                                size={36}
                            /> {row?.row?.original?.data?.display_name}</Flex>
                        <Flex className="typeColumn" gap="12" fontSize="13px">
                            <Paragraph fontSize={1} color="gray.60" my={0} flex={1}>
                                {row?.row?.original?.data?.registration_number}
                            </Paragraph>
                            <Paragraph fontSize={1} color="gray.60" my={0} flex={1}>
                                {row?.row?.original?.data?.entity_type ? row?.row?.original?.data?.entity_type : 'Individual'}
                            </Paragraph>

                        </Flex>
                    </>
                );
            },
        },
        {
            header: () => "",
            accessorKey: "Connections",
            cell: (row) => {
                const edges = row.table?.options?.meta?.edges || [];
                const connectionsCount = edges.filter(edge => edge.source === row.row.original?.id).length;
                return (
                    <>
                        <Flex className="typeColumn" alignItems="center" gap="6">
                            Connections</Flex>
                        <Flex gap="12" fontSize="13px">
                            <Paragraph fontSize={1} color="black" my={0} flex={1}>
                                {connectionsCount}
                            </Paragraph>

                        </Flex>
                    </>
                );
            },
        },
        {
            header: () => "",
            accessorKey: "Depth",
            cell: (info) => {
                return (
                    <>
                        <Flex className="typeColumn" alignItems="center" gap="6">
                            Depth</Flex>
                        <Flex gap="12" fontSize="13px">
                            <Paragraph fontSize={1} color="black" my={0} flex={1}>
                                {info?.row?.original?.depth}
                            </Paragraph>

                        </Flex>
                    </>
                );
            },
        },
        {
            id: 'expander',
            header: () => null,
            cell: ({ row }) => (
                <button onClick={row.getToggleExpandedHandler()}
                    style={{ cursor: 'pointer', background: 'none', border: 'none', fontSize: '16px' }} >
                    {row.getIsExpanded() ? <Icon
                        Type={CgChevronUp}
                        aria-hidden="true"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                    /> : <Icon
                        Type={CgChevronDown}
                        aria-hidden="true"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                    />}
                </button>
            )

        }
    ];

    const [columns] = useState([...cols]);
    const [{ pageIndex, pageSize }, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });

    const [sorting, setSorting] = useState<SortingState>([
        { id: "created_on", desc: true },
    ]);


    useEffect(() => {
        setPagination({ pageIndex: 0, pageSize: 10 });
    }, [searchTerm, filterUserIds, filters]);

    useEffect(() => {
        setData(() => fetchedorg);
    }, [fetchedorg]);

    const pagination = useMemo(
        () => ({
            pageIndex,
            pageSize,
        }),
        [pageIndex, pageSize]
    );
    const table = useReactTable({
        data: data?.nodes,
        columns,
        state: {
            pagination,
            sorting,
            expanded
        },
        pageCount: data?.total_nodes ? Math.ceil(data?.total_nodes / pageSize) : -1,
        debugTable: true,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onExpandedChange: setExpanded,
        manualPagination: false,
        manualSorting: true,
        getRowCanExpand: () => true,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        autoResetExpanded: true,
        meta: {
            edges: data?.edges ?? [],
        }
    });
    const getTargetNodeName = (targetId: number) => {
        return data?.nodes?.find(node => node.id === targetId)?.data?.display_name || '-'
    };

    const getTargetType = (targetId: number) => {
        const edge = data?.nodes?.find(node => node.id === targetId);
        return edge?.data?.entity_type;
    }
    const getTargetRegistrationNumber = (targetId: number) => {
        const edge = data?.nodes?.find(node => node.id === targetId);
        const type = edge?.type;
        return type === "entity" ? edge?.data?.registration_number || '-' : '';
    };

    const getTargetIsEntity = (targetId: number) => {
        return data?.nodes?.find(node => node.id === targetId)?.type === "entity";
    }

    return (
        <div>
            {(orgQuery.isFetching) && (
                <Flex justifyContent="center" alignItems="center" minHeight="25vh">
                    <Loader />
                </Flex>
            )}
            {!orgQuery.isFetching &&
                data?.results?.length === 0 && (
                    <Paragraph m={3} color="gray.40" textAlign="center">
                        No Organisation Data Available
                    </Paragraph>
                )}

            {data?.nodes?.length > 0 && (
                <div className="tableContainer" style={{ height: "600px" }}>
                    <table>
                        <thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <th key={header.id} colSpan={header.colSpan}>
                                            {header.isPlaceholder ? null : (
                                                <>
                                                    <div
                                                        {...{
                                                            className: header.column.getCanSort()
                                                                ? "select-none cursor-pointer flex items-center gap-1"
                                                                : "",
                                                            onClick: header.column.getToggleSortingHandler(),
                                                        }}
                                                    >
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                        {{
                                                            asc: (
                                                                <Icon
                                                                    display="inline-block"
                                                                    ml={1}
                                                                    size="20px"
                                                                    color="gray.40"
                                                                    Type={FaSortDown}
                                                                ></Icon>
                                                            ),
                                                            desc: (
                                                                <Icon
                                                                    display="inline-block"
                                                                    ml={1}
                                                                    size="20px"
                                                                    color="gray.40"
                                                                    Type={FaSortUp}
                                                                ></Icon>
                                                            ),
                                                        }[header.column.getIsSorted() as string] ?? null}
                                                    </div>
                                                </>
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.map((row) => {
                                const relatedEdge = data?.edges?.filter(
                                    edge => edge.source === row.original.id
                                );

                                return (
                                    <React.Fragment key={row.id}>
                                        <tr>
                                            {row.getVisibleCells().map((cell) => (
                                                <td key={cell.id}>
                                                    {flexRender(
                                                        cell.column.columnDef.cell,
                                                        cell.getContext()
                                                    )}
                                                </td>
                                            ))}
                                        </tr>
                                        {row.getIsExpanded() && relatedEdge?.length === 0 && (
                                            <tr style={{ background: '#E4E5F1', borderRadius: '2' }}>
                                                <td colSpan={row.getVisibleCells().length} style={{ padding: 0 }}>
                                                    <div style={{ fontSize: 13, background: '#f5f7fa', padding: 12, margin: 12, borderRadius: 6 }}>No associated profiles found</div>
                                                </td>
                                            </tr>

                                        )}
                                        {row.getIsExpanded() && relatedEdge?.length > 0 && relatedEdge?.map(edge => {
                                            const targetName = getTargetNodeName(edge.target);
                                            const targetRegistrationNumber = getTargetRegistrationNumber(edge.target);
                                            const targetType = getTargetType(edge.target);
                                            const targetIsEntity = getTargetIsEntity(edge.target);

                                            return (
                                                <tr key={edge.id} style={{ background: '#E4E5F1', borderRadius: '2' }}>
                                                    <td colSpan={row.getVisibleCells().length} style={{ padding: 0 }}>
                                                        <div style={{ fontSize: 13, background: '#f5f7fa', padding: 12, margin: 12, borderRadius: 6 }}>
                                                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1fr', paddingBottom: 8, color: '#828b9d' }}>
                                                                <Flex alignItems={"center"} gap="8">
                                                                    <SubjectTypeIcon
                                                                        isEntity={targetIsEntity}
                                                                        size={36}
                                                                    />
                                                                    {targetName}
                                                                </Flex>
                                                                <div style={{ color: '#828b9d' }}>Relationship type</div>
                                                                <div style={{ color: '#828b9d' }}>Percent</div>
                                                                <div style={{ color: '#828b9d' }}>Description</div>
                                                                <div style={{ color: '#828b9d', marginLeft: '7%' }}>From</div>
                                                                <div style={{ color: '#828b9d' }}>To </div>
                                                                <div style={{ color: '#828b9d' }}>Status</div>
                                                            </div>
                                                            {edge.data.relationships.map(rel => (
                                                                <div key={rel.edge_id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1fr', padding: '8px 0' }}>
                                                                    <div style={{ color: '#828b9d', marginLeft: '14%' }}>
                                                                        <div>{targetType}</div>
                                                                        <div style={{marginTop: '2%'}}>{targetRegistrationNumber}</div>
                                                                    </div>
                                                                    <div>{rel.relationship_type.name ? rel.relationship_type.name : "-"}</div>
                                                                    <div>{rel.percentage ? rel.percentage + '%' : '-'}</div>
                                                                    <div>{rel.description ? rel.description: "-"}</div>
                                                                    <div style={{ marginLeft: '5%' }}>{formatDate(rel.from_date)}</div>
                                                                    <div>{formatDate(rel.to_date)}</div>
                                                                    <div>{rel.active ? 'Active' : 'Inactive'}</div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </React.Fragment>

                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            <Flex
                className="pagination"
                justifyContent="space-between"
                alignItems="center"
            >
                <Flex>
                    <span>
                        Rows per page &nbsp;&nbsp;
                        <select
                            className="pageSizeDropdown"
                            value={table.getState().pagination.pageSize}
                            onChange={(e) => table.setPageSize(Number(e.target.value))}
                        >
                            {[5, 10, 20, 50, 100].map((pageSize) => (
                                <option key={pageSize} value={pageSize}>
                                    {pageSize}
                                </option>
                            ))}
                        </select>
                    </span>
                </Flex>

                <Flex>
                    <span style={{ display: "flex", alignItems: "center" }}>
                        {table.getState().pagination.pageIndex + 1} of{" "}
                        {table.getPageCount()}
                    </span>
                    &nbsp;&nbsp;&nbsp;
                    <button
                        disabled={!table.getCanPreviousPage()}
                        onClick={() => table.setPageIndex(0)}
                    >
                        &lt;&lt;
                    </button>
                    &nbsp;
                    <button
                        disabled={!table.getCanPreviousPage()}
                        onClick={() => table.previousPage()}
                    >
                        &lt;
                    </button>
                    &nbsp;
                    <button
                        disabled={!table.getCanNextPage()}
                        onClick={() => table.nextPage()}
                    >
                        &gt;
                    </button>
                    &nbsp;
                    <button
                        disabled={!table.getCanNextPage()}
                        onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                    >
                        &gt;&gt;
                    </button>
                </Flex>
            </Flex>
        </div>
    );
};
